import { useMemo, useRef } from 'react'
import { find, maxBy, minBy, toNumber } from 'lodash-es'
import { Vector3 } from 'three'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useModelStore, useResultsStore, useStiffeningStore } from '@editorStores'
import { useCameraEvent, useGuidToElementIncludingRips } from '@editorHooks'

const VERTICAL_DOT_THRESHOLD = 0.7
const MIN_LINE_MINOR_EXTENT = 0.5
const FALLBACK_HORIZONTAL_REF: [number, number, number] = [1, 0, 0]
const ALT_HORIZONTAL_REF: [number, number, number] = [0, 1, 0]
// Multiplier on the perspective-fit distance to leave room around the selected element so the
// surrounding context (neighbouring rips, openings, beams, …) is visible.
const SELECTION_PADDING_SHAPE = 2
const SELECTION_PADDING_LINE = 5

type Point3 = { x: number; y: number; z: number }

const toVector3 = (p: Point3) => new Vector3(p.x, p.y, p.z)

const buildEvent = (
  center: Vector3,
  right: Vector3,
  up: Vector3,
  width: number,
  height: number,
  padding: number,
): OrientedFitPerspectiveCameraEvent => ({
  cameraType: 'perspective',
  type: 'orientedFitPerspective',
  target: [center.x, center.y, center.z],
  right: [right.x, right.y, right.z],
  up: [up.x, up.y, up.z],
  size: { width, height },
  padding,
  priority: 2,
})

/**
 * Compute the unit normal of a polygon from the first non-degenerate triangle.
 * Returns null if the polygon is degenerate (all points collinear).
 */
const computePolygonNormal = (points: Vector3[]): Vector3 | null => {
  if (points.length < 3) return null
  const p0 = points[0]
  const edge0 = points[1].clone().sub(p0)
  if (edge0.length() < 1e-4) return null
  for (let i = 2; i < points.length; i++) {
    const candidate = new Vector3().crossVectors(edge0, points[i].clone().sub(p0))
    if (candidate.length() > 1e-4) {
      return candidate.normalize()
    }
  }
  return null
}

const polygonExtents = (points: Vector3[], right: Vector3, up: Vector3) => {
  let minR = Infinity
  let maxR = -Infinity
  let minU = Infinity
  let maxU = -Infinity
  for (const p of points) {
    const r = p.x * right.x + p.y * right.y + p.z * right.z
    const u = p.x * up.x + p.y * up.y + p.z * up.z
    if (r < minR) minR = r
    if (r > maxR) maxR = r
    if (u < minU) minU = u
    if (u > maxU) maxU = u
  }
  return { minR, maxR, minU, maxU }
}

const buildLineEvent = (start: Point3, end: Point3): OrientedFitPerspectiveCameraEvent | null => {
  const startVec = toVector3(start)
  const endVec = toVector3(end)
  const direction = endVec.clone().sub(startVec)
  const length = direction.length()

  if (length < 1e-4) return null

  const directionN = direction.clone().normalize()
  const center = startVec.clone().add(endVec).multiplyScalar(0.5)
  const isVertical = Math.abs(directionN.z) >= VERTICAL_DOT_THRESHOLD

  let right: Vector3
  let up: Vector3
  let width: number
  let height: number

  if (isVertical) {
    // Vertical lines: keep the line vertical on screen, start at bottom & end at top.
    up = directionN
    const refRight = new Vector3(
      ...(Math.abs(up.x) < 0.9 ? FALLBACK_HORIZONTAL_REF : ALT_HORIZONTAL_REF),
    )
    right = refRight.sub(up.clone().multiplyScalar(refRight.dot(up))).normalize()
    width = MIN_LINE_MINOR_EXTENT
    height = length
  } else {
    // Horizontal-ish lines: keep the line horizontal, start on the left & end on the right.
    right = directionN
    const refUp = new Vector3(0, 0, 1)
    up = refUp.sub(right.clone().multiplyScalar(refUp.dot(right))).normalize()
    width = length
    height = MIN_LINE_MINOR_EXTENT
  }

  return buildEvent(center, right, up, width, height, SELECTION_PADDING_LINE)
}

const buildPolygonEvent = (rawPoints: Point3[]): OrientedFitPerspectiveCameraEvent | null => {
  if (rawPoints.length < 3) return null

  const points = rawPoints.map(toVector3)
  const p0 = points[0]
  const p1 = points[1]
  const edge = p1.clone().sub(p0)
  if (edge.length() < 1e-4) return null

  // First polygon point on the left, second on the right.
  const right = edge.clone().normalize()

  const normal = computePolygonNormal(points)
  if (!normal) return null

  const up = new Vector3().crossVectors(normal, right).normalize()
  if (up.length() < 1e-4) return null

  // Frame the polygon by its bounding rect in (right, up) space, so non-square shapes
  // (e.g. trapezoid slabs) still get fit nicely.
  const { minR, maxR, minU, maxU } = polygonExtents(points, right, up)

  const centerR = (minR + maxR) / 2
  const centerU = (minU + maxU) / 2
  // The plane the polygon lies on, expressed via its signed distance to the origin in normal space.
  const planeOffset = p0.x * normal.x + p0.y * normal.y + p0.z * normal.z

  const target = new Vector3()
    .addScaledVector(right, centerR)
    .addScaledVector(up, centerU)
    .addScaledVector(normal, planeOffset)

  return buildEvent(target, right, up, maxR - minR, maxU - minU, SELECTION_PADDING_SHAPE)
}

/** Same geometry as `SimpleStiffeningMesh` — quadrilateral of the stiffening panel on the wall. */
const stiffeningIntervalToPolygonPoints = (
  interval: StiffeningSegment,
  wall: ShapeObject,
): Point3[] | null => {
  const pts = wall.shape?.points
  if (!pts || pts.length < 2) return null

  const wallPointsVec = pts.map(p => toVector3(p as Point3))
  const initialStart = wallPointsVec[0]
  const wallEnd = wallPointsVec[1]
  const direction = wallEnd.clone().sub(initialStart).normalize()

  let height: number
  if (interval.effective_height === undefined) {
    const zMax = maxBy(pts, p => (p as Point3).z)?.z ?? 0
    const zMin = minBy(pts, p => (p as Point3).z)?.z ?? 0
    height = zMax - zMin
  } else {
    height = interval.effective_height
  }

  const bottomLeft = initialStart
    .clone()
    .add(direction.clone().multiplyScalar(toNumber(interval.interval.lower)))
  const span = toNumber(interval.interval.upper) - toNumber(interval.interval.lower)
  const bottomRight = bottomLeft.clone().add(direction.clone().multiplyScalar(span))
  const heightOffset = new Vector3(0, 0, height)
  const topRight = bottomRight.clone().add(heightOffset)
  const topLeft = bottomLeft.clone().add(heightOffset)

  return [bottomLeft, bottomRight, topRight, topLeft].map(v => ({ x: v.x, y: v.y, z: v.z }))
}

/**
 * Frames a vertical line element so the camera is orthogonal to a parent polygon (e.g. a wall).
 * - `up` is the line direction (start at the bottom, end at the top).
 * - The camera looks along the polygon normal, so we always face the wall (not edge-on).
 * - The framing extent is the line itself, padded for context.
 */
const buildVerticalLineFacingPolygonEvent = (
  lineStart: Point3,
  lineEnd: Point3,
  rawPolygonPoints: Point3[],
): OrientedFitPerspectiveCameraEvent | null => {
  if (rawPolygonPoints.length < 3) return null

  const startVec = toVector3(lineStart)
  const endVec = toVector3(lineEnd)
  const direction = endVec.clone().sub(startVec)
  const length = direction.length()
  if (length < 1e-4) return null

  const points = rawPolygonPoints.map(toVector3)
  const normal = computePolygonNormal(points)
  if (!normal) return null

  const up = direction.clone().normalize()
  // `back = right × up`, so picking right = up × back puts the camera back along `normal`.
  // I.e. the camera looks down `-normal`, so the wall is face-on.
  const right = new Vector3().crossVectors(up, normal).normalize()
  if (right.length() < 1e-4) {
    // The line is parallel to the wall normal — fall back to default vertical framing.
    return buildLineEvent(lineStart, lineEnd)
  }

  const center = startVec.clone().add(endVec).multiplyScalar(0.5)
  return buildEvent(center, right, up, MIN_LINE_MINOR_EXTENT, length, SELECTION_PADDING_LINE)
}

type GuidElement = ShapeObject | ShapeObjectLine | Rip | Lintel

const isLineShaped = (element: GuidElement): element is ShapeObjectLine =>
  'shape' in element && element.shape != null && 'start' in element.shape && 'end' in element.shape

const isPolygonShaped = (element: GuidElement): element is ShapeObject =>
  'shape' in element && element.shape != null && 'points' in element.shape

const hasStartEnd = (element: GuidElement): element is Rip | Lintel =>
  'start' in element && 'end' in element

/**
 * Frames the selected Results2 element with a perspective camera so:
 * - Polygon elements (walls, slabs, …) are face-on with `points[0]` left & `points[1]` right.
 * - Horizontal line elements (beams, lintels, …) run left-to-right with `start` on the left.
 * - Vertical line elements (rips, columns, standard rips) run bottom-to-top with `start` at the bottom.
 * - Slab beams & roof slab beams use the same line framing as lintels (beam start left, end right
 *   for horizontal segments; vertical segments bottom→top).
 * - Wall rips and standard rips are framed orthogonal to their parent wall, so the wall is always
 *   face-on (never edge-on).
 *
 * Stiffening segments frame the segment’s wall panel (same quad as `SimpleStiffeningMesh`).
 *
 * When `fokusEnabled` is false, no camera event is emitted (list footer “Fokus” switch).
 */
export const useResults2CameraEvent = (
  selectedId: string | undefined,
  fokusEnabled: boolean,
): void => {
  const guidToElement = useGuidToElementIncludingRips()
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const walls = useModelStore(state => state.model.walls)
  const rips = useModelStore(state => state.model.rips)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  // The orthographic floorplan view (priority 1) and other ortho events drive their own framing,
  // so we suppress the selection-driven perspective fit while it is active.
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const previousEvent = useRef<OrientedFitPerspectiveCameraEvent | null>(null)

  const event = useMemo<OrientedFitPerspectiveCameraEvent | null>(() => {
    if (!selectedId || isOrthographic || !fokusEnabled) return null

    // Slab/roof-slab beam → same framing as lintels (beam along screen horizontal where applicable).
    const parentSlab =
      verticalSlabs.find(slab => slab.beam.guid === selectedId) ??
      verticalRoofSlabs.find(slab => slab.beam.guid === selectedId)
    if (parentSlab) {
      const { start, end } = parentSlab.beam.shape
      const event = buildLineEvent(start, end)
      previousEvent.current = event
      return event
    }

    // Wall rip → use the parent wall's plane so the camera is always orthogonal to the wall.
    const rip = find(rips, r => r.position_guid === selectedId)
    if (rip) {
      const wall = find(walls, w => w.guid === rip.wall_guid)
      if (wall) {
        const event = buildVerticalLineFacingPolygonEvent(rip.start, rip.end, wall.shape.points)
        previousEvent.current = event
        return event
      }
      return buildLineEvent(rip.start, rip.end)
    }

    // Standard rip → also a wall member; align with the parent wall.
    const standardRipCheck = find(
      standardRipChecks,
      check => check.standard_rip_member.guid === selectedId,
    )
    if (standardRipCheck) {
      const { start, end } = standardRipCheck.standard_rip_member
      const wall = find(walls, w => w.guid === standardRipCheck.wall_guid)
      if (wall) {
        const event = buildVerticalLineFacingPolygonEvent(start, end, wall.shape.points)
        previousEvent.current = event
        return event
      }
      return buildLineEvent(start, end)
    }

    const stiffeningInterval = find(stiffeningIntervals, { guid: selectedId })
    if (stiffeningInterval) {
      const wall = find(walls, w => w.guid === stiffeningInterval.element_guid)
      if (wall && isPolygonShaped(wall as GuidElement)) {
        const quad = stiffeningIntervalToPolygonPoints(stiffeningInterval, wall as ShapeObject)
        if (quad) {
          const event = buildPolygonEvent(quad)
          previousEvent.current = event
          return event
        }
      }
    }

    const element = guidToElement[selectedId] as GuidElement | undefined
    if (element) {
      if (isLineShaped(element)) {
        const event = buildLineEvent(element.shape.start, element.shape.end)
        previousEvent.current = event
        return event
      }
      if (isPolygonShaped(element)) {
        const event = buildPolygonEvent(element.shape.points)
        previousEvent.current = event
        return event
      }
      if (hasStartEnd(element)) {
        // Lintels (and any other position_guid-keyed line) — fall back to plain line framing.
        const event = buildLineEvent(element.start, element.end)
        previousEvent.current = event
        return event
      }
    }

    return null
  }, [
    selectedId,
    isOrthographic,
    fokusEnabled,
    verticalSlabs,
    verticalRoofSlabs,
    walls,
    rips,
    standardRipChecks,
    stiffeningIntervals,
    guidToElement,
  ])

  const renderedEvent = useMemo(() => {
    if (event) return event
    if (previousEvent.current && !isOrthographic) return previousEvent.current

    return null
  }, [event, previousEvent, isOrthographic])

  useCameraEvent(renderedEvent)
}

export default useResults2CameraEvent
